import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const HomeAppSelection = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);       // 'emit', 'sage', 'champ', 'ship', 'safe', 'family'
  const [expandedApp, setExpandedApp] = useState(null); // 'emit_sage' or 'mental_performance'

  const apps = [
    {
      id: 'emit_sage',
      icon: '✨',
      title: 'Emit / SAGE',
      desc: 'Biblical Wisdom or Spiritual Wisdom',
      color: '#4f46e5',
      gradient: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
      subOptions: [
        { id: 'emit', icon: '📖', label: 'Emit', desc: 'Biblical & Ancient Wisdom — Faith-based', color: '#4f46e5' },
        { id: 'sage', icon: '🌿', label: 'SAGE', desc: 'Spiritual Wisdom — Interfaith & Secular', color: '#059669' }
      ]
    },
    {
      id: 'mental_performance',
      icon: '⚡',
      title: 'Mental Performance',
      desc: 'CHAMP (Biblical) or SHIP (Mindfulness)',
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
      subOptions: [
        { id: 'champ', icon: '🏆', label: 'CHAMP', desc: 'Biblical Foundation — Faith-based Performance', color: '#f59e0b' },
        { id: 'ship', icon: '🧘', label: 'SHIP', desc: 'Mindfulness Foundation — Secular Performance', color: '#06b6d4' }
      ]
    },
    {
      id: 'safe',
      icon: '💚',
      title: 'SAFE',
      desc: 'Emotional & Mental Wellness',
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981, #059669)',
      subOptions: null
    },
    {
      id: 'family',
      icon: '👨‍👩‍👧',
      title: 'Family Coach',
      desc: 'Family Wellness & Relationship Support',
      color: '#3b82f6',
      gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)',
      subOptions: null
    }
  ];

  // Map selection to route
  const getRoute = (sel) => {
    const routes = {
      emit: '/emit-the-bible',
      sage: '/emit-the-bible',
      champ: '/mental-performance/setup',
      ship: '/mental-performance/setup',
      safe: '/safe/setup',
      family: '/family-coach/setup'
    };
    return routes[sel] || '/emit-the-bible';
  };

  const handleAppClick = (app) => {
    if (app.subOptions) {
      // Toggle sub-options
      setExpandedApp(expandedApp === app.id ? null : app.id);
      setSelected(null);
    } else {
      // Direct selection
      setExpandedApp(null);
      setSelected(app.id);
    }
  };

  const handleSubSelect = (subId) => {
    setSelected(subId);
  };

  const handleContinue = async () => {
    if (!selected) return;
    // Save to localStorage
    localStorage.setItem('home_app', selected);
    // Save to backend (source of truth)
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/v1/auth/set-home-app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ home_app: selected })
      });
    } catch (e) {
      console.warn('Could not save home_app to server:', e);
    }
    // Navigate to that app's setup/chat
    navigate(getRoute(selected));
  };

  const isAppSelected = (app) => {
    if (app.subOptions) {
      return app.subOptions.some(sub => sub.id === selected);
    }
    return selected === app.id;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif"
    }}>

      {/* Ambient glow */}
      <div style={{ position: 'fixed', top: '15%', left: '10%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(79,70,229,0.15) 0%, transparent 70%)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', bottom: '15%', right: '10%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: '520px', width: '100%', position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏠</div>
          <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '700', marginBottom: '8px', margin: '0 0 8px 0' }}>
            Choose Your Home App
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '15px', margin: '0 0 8px 0', lineHeight: '1.5' }}>
            This is where your journey begins every time you log in.
          </p>
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '13px', margin: 0 }}>
            You can change this anytime from the menu inside any app.
          </p>
        </div>

        {/* App Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
          {apps.map(app => (
            <div key={app.id}>
              {/* Main card */}
              <div
                onClick={() => handleAppClick(app)}
                style={{
                  padding: '18px 20px',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background: isAppSelected(app)
                    ? `${app.color}22`
                    : expandedApp === app.id
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(255,255,255,0.05)',
                  border: isAppSelected(app)
                    ? `2px solid ${app.color}`
                    : expandedApp === app.id
                    ? '1px solid rgba(255,255,255,0.25)'
                    : '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '12px',
                    background: app.gradient,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '22px', flexShrink: 0
                  }}>
                    {app.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>{app.title}</div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginTop: '2px' }}>{app.desc}</div>
                  </div>
                  {app.subOptions ? (
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '18px', transition: 'transform 0.2s', transform: expandedApp === app.id ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ›
                    </span>
                  ) : (
                    isAppSelected(app) && <span style={{ color: app.color, fontSize: '20px' }}>✓</span>
                  )}
                </div>
              </div>

              {/* Sub-options (expanded) */}
              {app.subOptions && expandedApp === app.id && (
                <div style={{
                  marginTop: '6px',
                  marginLeft: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {app.subOptions.map(sub => (
                    <div
                      key={sub.id}
                      onClick={() => handleSubSelect(sub.id)}
                      style={{
                        padding: '14px 16px',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        background: selected === sub.id ? `${sub.color}22` : 'rgba(255,255,255,0.04)',
                        border: selected === sub.id ? `2px solid ${sub.color}` : '1px solid rgba(255,255,255,0.08)',
                        display: 'flex', alignItems: 'center', gap: '12px'
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>{sub.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: 'white', fontWeight: '600', fontSize: '15px' }}>{sub.label}</div>
                        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', marginTop: '2px' }}>{sub.desc}</div>
                      </div>
                      {selected === sub.id && <span style={{ color: sub.color, fontSize: '18px' }}>✓</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinue}
          disabled={!selected}
          style={{
            width: '100%',
            padding: '17px',
            fontSize: '16px',
            fontWeight: '600',
            background: selected
              ? 'linear-gradient(135deg, #4f46e5, #7c3aed)'
              : 'rgba(255,255,255,0.08)',
            color: selected ? 'white' : 'rgba(255,255,255,0.3)',
            border: 'none',
            borderRadius: '14px',
            cursor: selected ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
            boxShadow: selected ? '0 4px 20px rgba(79,70,229,0.4)' : 'none'
          }}
        >
          {selected ? `Set ${
            selected === 'emit' ? 'Emit' :
            selected === 'sage' ? 'SAGE' :
            selected === 'champ' ? 'CHAMP' :
            selected === 'ship' ? 'SHIP' :
            selected === 'safe' ? 'SAFE' :
            'Family Coach'
          } as My Home App →` : 'Select Your Home App'}
        </button>

        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px', marginTop: '16px' }}>
          All apps are always accessible from the dropdown menu
        </p>

      </div>
    </div>
  );
};

export default HomeAppSelection;
